import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { format } from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";
import {
  buildCountryRows,
  buildCsvContent,
  buildProductRows,
  COUNTRY_EXPORT_KEYS,
  downloadCsv,
  PRODUCT_EXPORT_KEYS,
} from "../utils/analytics-csv";

type ExportDimension = "products" | "countries";

export function AnalyticsExportButton() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const {
    dateRange,
    filters,
    productFilter,
    includeAllOrders,
    isAnyFilterApplied,
  } = useAnalyticsFilters();
  const [isExporting, setIsExporting] = useState(false);

  const buildQueryParams = (filtered: boolean) => {
    const params: Record<string, unknown> = {
      fromTimestamp: dateRange.from,
      toTimestamp: dateRange.to,
    };

    if (filtered) {
      const resolution = filters.get("resolution")?.value.join(",");
      const reason = filters.get("reason")?.value.join(",");
      const country = filters.get("country")?.value.join(",");
      const sourceChannel = filters.get("sourceChannel")?.value.join(",");
      const productTags = filters.get("productTag")?.value.join(",");

      if (resolution) params.resolution = resolution;
      if (reason) params.reason = reason;
      if (country) params.country = country;
      if (sourceChannel) params.sourceChannel = sourceChannel;
      if (productTags) params.productTags = productTags;
      if (productFilter?.productId) params.productId = productFilter.productId;
      if (productFilter?.catalogId) params.catalogId = productFilter.catalogId;
      if (includeAllOrders) params.includeAllOrders = true;
    }

    return params;
  };

  const exportCsv = async (filtered: boolean, dimension: ExportDimension) => {
    setIsExporting(true);
    try {
      const { data, error } = await fetchClient.GET(
        "/shops/{shopId}/statistics/products-return-rates",
        {
          params: {
            path: { shopId: shop.id },
            query: buildQueryParams(filtered),
          },
        },
      );

      if (error || !data) return;

      const today = format(new Date(), "yyyy-MM-dd");

      if (dimension === "countries") {
        // The endpoint returns every country even when a country filter is
        // active, so the selection is applied here.
        const selectedCountryCodes = filtered
          ? (filters.get("country")?.value ?? [])
          : [];
        const rows = buildCountryRows(
          data.countries ?? [],
          selectedCountryCodes,
          t("analytics.export.noShippingAddress"),
        );
        if (rows.length === 0) return;

        downloadCsv(
          buildCsvContent(rows, COUNTRY_EXPORT_KEYS),
          `analytics-countries-${today}.csv`,
        );
        return;
      }

      const rows = buildProductRows(data.products ?? []);
      if (rows.length === 0) return;

      downloadCsv(
        buildCsvContent(rows, PRODUCT_EXPORT_KEYS),
        `analytics-${today}.csv`,
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          disabled={isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t("analytics.export.products")}</DropdownMenuLabel>
        {isAnyFilterApplied && (
          <DropdownMenuItem onClick={() => exportCsv(true, "products")}>
            {t("analytics.export.filtered")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => exportCsv(false, "products")}>
          {t("analytics.export.all")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("analytics.export.countries")}</DropdownMenuLabel>
        {isAnyFilterApplied && (
          <DropdownMenuItem onClick={() => exportCsv(true, "countries")}>
            {t("analytics.export.countriesFiltered")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => exportCsv(false, "countries")}>
          {t("analytics.export.countriesAll")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
